import { useState, useEffect, useCallback } from "react";
import api, { getAssetsUrl } from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

interface SegRule {
  tipo_segmento: string;
  valor_segmento?: string | null;
}

interface AnuncioRow {
  id: number;
  imagenUrl: string;
  texto?: string | null;
  enlaceUrl?: string | null;
  esPrioritario?: boolean;
  isActive?: boolean;
  segmentacion?: { tipoSegmento?: string; valorSegmento?: string | null }[];
}

const TIPOS_SEG = [
  { value: "todos", label: "Todos" },
  { value: "cliente", label: "Cliente específico" },
  { value: "modalidad", label: "Modalidad" },
  { value: "tipo_seguro", label: "Tipo seguro" },
];

// El cuerpo completo debe quedar por debajo del límite de 1 MiB de Nginx.
const MAX_IMAGE_SIZE = 900 * 1024;

export function AnunciosPage() {
  const [list, setList] = useState<AnuncioRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState<{ id: number; razonSocial?: string }[]>([]);
  const [modalidades, setModalidades] = useState<{ id: number; nombre: string }[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ texto: "", enlace_url: "", es_prioritario: false, is_active: true });
  const [segmentacion, setSegmentacion] = useState<SegRule[]>([{ tipo_segmento: "todos" }]);
  const [imagenFile, setImagenFile] = useState<File | null>(null);
  const [imagenReplaceId, setImagenReplaceId] = useState<number | null>(null);
  const [segModalOpen, setSegModalOpen] = useState(false);
  const [segEditId, setSegEditId] = useState<number | null>(null);
  const [segRules, setSegRules] = useState<SegRule[]>([{ tipo_segmento: "todos" }]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [replaceError, setReplaceError] = useState("");

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<AnuncioRow[]>("/admin/anuncios");
      setList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClientes = useCallback(async () => {
    try {
      const res = await api.get<{ data: { id: number; razonSocial?: string }[] }>("/admin/clientes", { params: { limit: 200 } });
      setClientes(res.data?.data ?? []);
    } catch {
      setClientes([]);
    }
  }, []);

  const fetchModalidades = useCallback(async () => {
    try {
      const res = await api.get<{ id: number; nombre: string }[]>("/admin/modalidades", { params: { incluir_inactivas: "true" } });
      setModalidades(Array.isArray(res.data) ? res.data : []);
    } catch {
      setModalidades([]);
    }
  }, []);

  useEffect(() => {
    fetchList();
    fetchClientes();
    fetchModalidades();
  }, [fetchList, fetchClientes, fetchModalidades]);

  const openEdit = (a: AnuncioRow) => {
    setEditingId(a.id);
    setForm({
      texto: a.texto ?? "",
      enlace_url: a.enlaceUrl ?? "",
      es_prioritario: !!a.esPrioritario,
      is_active: !!a.isActive,
    });
    setSegmentacion(
      (a.segmentacion?.length ? a.segmentacion : [{ tipoSegmento: "todos", valorSegmento: null }]).map((s) => ({
        tipo_segmento: s.tipoSegmento ?? "todos",
        valor_segmento: s.valorSegmento ?? undefined,
      }))
    );
    setModalOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ texto: "", enlace_url: "", es_prioritario: false, is_active: true });
    setSegmentacion([{ tipo_segmento: "todos" }]);
    setImagenFile(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setError("");
    setActionLoading(true);
    try {
      if (editingId != null) {
        await api.patch(`/admin/anuncios/${editingId}`, {
          texto: form.texto || null,
          enlace_url: form.enlace_url || null,
          es_prioritario: form.es_prioritario,
          is_active: form.is_active,
        });
        setModalOpen(false);
        fetchList();
      } else {
        if (!imagenFile) {
          setError("Selecciona una imagen");
          setActionLoading(false);
          return;
        }
        if (imagenFile.size > MAX_IMAGE_SIZE) {
          setError("La imagen no puede superar 900 KB");
          setActionLoading(false);
          return;
        }
        const fd = new FormData();
        fd.append("imagen", imagenFile);
        fd.append("texto", form.texto);
        fd.append("enlace_url", form.enlace_url);
        fd.append("es_prioritario", String(form.es_prioritario));
        fd.append("is_active", String(form.is_active));
        fd.append("segmentacion", JSON.stringify(segmentacion.filter((s) => s.tipo_segmento)));
        await api.post("/admin/anuncios", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setModalOpen(false);
        setImagenFile(null);
        fetchList();
      }
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReplaceImage = async () => {
    if (imagenReplaceId == null || !imagenFile) return;
    setReplaceError("");
    if (imagenFile.size > MAX_IMAGE_SIZE) {
      setReplaceError("La imagen no puede superar 900 KB");
      return;
    }
    setActionLoading(true);
    try {
      const fd = new FormData();
      fd.append("imagen", imagenFile);
      await api.patch(`/admin/anuncios/${imagenReplaceId}/imagen`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImagenReplaceId(null);
      setImagenFile(null);
      fetchList();
    } catch (err: unknown) {
      setReplaceError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "No se ha podido subir la imagen"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openSegModal = (id: number, segs: SegRule[]) => {
    setSegEditId(id);
    setSegRules(segs.length ? segs : [{ tipo_segmento: "todos" }]);
    setSegModalOpen(true);
  };

  const handleSaveSegmentacion = async () => {
    if (segEditId == null) return;
    setActionLoading(true);
    try {
      await api.patch(`/admin/anuncios/${segEditId}/segmentacion`, { segmentacion: segRules });
      setSegModalOpen(false);
      setSegEditId(null);
      fetchList();
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    try {
      await api.delete(`/admin/anuncios/${deleteId}`);
      setDeleteId(null);
      fetchList();
    } catch {
      setDeleteId(null);
    }
  };

  const segmentacionLabel = (s: { tipoSegmento?: string; valorSegmento?: string | null }) => {
    const t = s.tipoSegmento ?? "todos";
    if (t === "todos") return "Todos";
    if (t === "cliente") return `Cliente: ${clientes.find((c) => String(c.id) === s.valorSegmento)?.razonSocial ?? s.valorSegmento ?? ""}`;
    if (t === "modalidad") return `Modalidad: ${modalidades.find((m) => String(m.id) === s.valorSegmento)?.nombre ?? s.valorSegmento ?? ""}`;
    if (t === "tipo_seguro") return `Tipo: ${s.valorSegmento === "diario" ? "Diario" : s.valorSegmento === "anual" ? "Anual" : s.valorSegmento ?? ""}`;
    return t;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#2C2C2C]">Anuncios</h1>
      <Button onClick={openNew}>Nuevo anuncio</Button>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 h-64 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((a) => (
            <div key={a.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
              <div className="relative aspect-video w-full overflow-hidden bg-gray-100">
                <img src={getAssetsUrl(a.imagenUrl)} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement?.querySelector(".img-fallback")?.classList.remove("hidden"); }} />
                <div className="img-fallback hidden absolute inset-0 flex items-center justify-center text-sm text-gray-400">Sin imagen</div>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex gap-2 flex-wrap">
                  {a.isActive ? <Badge variant="success">Activo</Badge> : <Badge variant="neutral">Inactivo</Badge>}
                  {a.esPrioritario && <Badge variant="warning">Prioritario</Badge>}
                </div>
                {a.texto && <p className="text-sm text-gray-700 line-clamp-2">{a.texto}</p>}
                {a.enlaceUrl && <a href={a.enlaceUrl} target="_blank" rel="noreferrer" className="text-sm text-[#1A3AB8] hover:underline truncate block">{a.enlaceUrl}</a>}
                <div className="flex flex-wrap gap-1">
                  {(a.segmentacion ?? []).map((s, i) => (
                    <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{segmentacionLabel(s)}</span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button size="sm" variant="secondary" onClick={() => openEdit(a)}>Editar</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setImagenReplaceId(a.id); setImagenFile(null); setReplaceError(""); }}>Cambiar imagen</Button>
                  <Button size="sm" variant="ghost" onClick={() => openSegModal(a.id, (a.segmentacion ?? []).map((s) => ({ tipo_segmento: s.tipoSegmento ?? "todos", valor_segmento: s.valorSegmento ?? undefined })))}>Editar segmentación</Button>
                  <Button size="sm" variant="danger" onClick={() => setDeleteId(a.id)}>Eliminar</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingId != null ? "Editar anuncio" : "Nuevo anuncio"} footer={
        <>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button loading={actionLoading} onClick={handleSave}>{editingId != null ? "Guardar" : "Crear"}</Button>
        </>
      }>
        <div className="space-y-4">
          <Input label="Texto" value={form.texto} onChange={(e) => setForm((f) => ({ ...f, texto: e.target.value }))} />
          <Input label="Enlace URL" value={form.enlace_url} onChange={(e) => setForm((f) => ({ ...f, enlace_url: e.target.value }))} />
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.es_prioritario} onChange={(e) => setForm((f) => ({ ...f, es_prioritario: e.target.checked }))} /> Prioritario</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} /> Activo</label>
          {editingId == null && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Imagen (JPG/PNG, máximo 900 KB)</label>
              <input type="file" accept="image/jpeg,image/png" onChange={(e) => setImagenFile(e.target.files?.[0] ?? null)} className="border rounded px-3 py-2 w-full" />
            </div>
          )}
          {editingId == null && (
            <div>
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium">Segmentación</span><Button type="button" size="sm" variant="ghost" onClick={() => setSegmentacion([...segmentacion, { tipo_segmento: "todos" }])}>Añadir regla</Button></div>
              {segmentacion.map((s, i) => (
                <div key={i} className="flex gap-2 items-center mb-2">
                  <select value={s.tipo_segmento} onChange={(e) => setSegmentacion((prev) => prev.map((r, j) => j === i ? { ...r, tipo_segmento: e.target.value } : r))} className="border rounded px-2 py-1 text-sm flex-1">
                    {TIPOS_SEG.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {s.tipo_segmento === "cliente" && <select value={s.valor_segmento ?? ""} onChange={(e) => setSegmentacion((prev) => prev.map((r, j) => j === i ? { ...r, valor_segmento: e.target.value || undefined } : r))} className="border rounded px-2 py-1 text-sm flex-1"><option value="">Seleccionar</option>{clientes.map((c) => <option key={c.id} value={c.id}>{c.razonSocial}</option>)}</select>}
                  {s.tipo_segmento === "modalidad" && <select value={s.valor_segmento ?? ""} onChange={(e) => setSegmentacion((prev) => prev.map((r, j) => j === i ? { ...r, valor_segmento: e.target.value || undefined } : r))} className="border rounded px-2 py-1 text-sm flex-1"><option value="">Seleccionar</option>{modalidades.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}</select>}
                  {s.tipo_segmento === "tipo_seguro" && <select value={s.valor_segmento ?? ""} onChange={(e) => setSegmentacion((prev) => prev.map((r, j) => j === i ? { ...r, valor_segmento: e.target.value || undefined } : r))} className="border rounded px-2 py-1 text-sm flex-1"><option value="">-</option><option value="diario">Diario</option><option value="anual">Anual</option></select>}
                  {segmentacion.length > 1 && (
                    <button type="button" aria-label="Eliminar regla" onClick={() => setSegmentacion((prev) => prev.filter((_, j) => j !== i))} className="text-red-600 hover:underline">×</button>
                  )}
                </div>
              ))}
            </div>
          )}
          {error && <p className="text-red-600 text-sm">{error}</p>}
        </div>
      </Modal>

      {imagenReplaceId != null && (
        <Modal isOpen onClose={() => { setImagenReplaceId(null); setImagenFile(null); setReplaceError(""); }} title="Cambiar imagen" footer={
          <>
            <Button variant="secondary" onClick={() => { setImagenReplaceId(null); setImagenFile(null); setReplaceError(""); }}>Cancelar</Button>
            <Button loading={actionLoading} disabled={!imagenFile} onClick={handleReplaceImage}>Subir</Button>
          </>
        }>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Imagen (JPG/PNG, máximo 900 KB)</label>
            <input type="file" accept="image/jpeg,image/png" onChange={(e) => { setImagenFile(e.target.files?.[0] ?? null); setReplaceError(""); }} className="border rounded px-3 py-2 w-full" />
            {replaceError && <p className="text-sm text-red-600">{replaceError}</p>}
          </div>
        </Modal>
      )}

      <Modal isOpen={segModalOpen} onClose={() => { setSegModalOpen(false); setSegEditId(null); }} title="Editar segmentación" footer={
        <>
          <Button variant="secondary" onClick={() => { setSegModalOpen(false); setSegEditId(null); }}>Cancelar</Button>
          <Button loading={actionLoading} onClick={handleSaveSegmentacion}>Guardar</Button>
        </>
      }>
        <div className="space-y-2">
          <Button type="button" size="sm" variant="ghost" onClick={() => setSegRules([...segRules, { tipo_segmento: "todos" }])}>Añadir regla</Button>
          {segRules.map((s, i) => (
            <div key={i} className="flex gap-2 items-center">
              <select value={s.tipo_segmento} onChange={(e) => setSegRules((prev) => prev.map((r, j) => j === i ? { ...r, tipo_segmento: e.target.value } : r))} className="border rounded px-2 py-1 text-sm flex-1">
                {TIPOS_SEG.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {s.tipo_segmento === "cliente" && <select value={s.valor_segmento ?? ""} onChange={(e) => setSegRules((prev) => prev.map((r, j) => j === i ? { ...r, valor_segmento: e.target.value || undefined } : r))} className="border rounded px-2 py-1 text-sm flex-1"><option value="">Cliente</option>{clientes.map((c) => <option key={c.id} value={c.id}>{c.razonSocial}</option>)}</select>}
              {s.tipo_segmento === "modalidad" && <select value={s.valor_segmento ?? ""} onChange={(e) => setSegRules((prev) => prev.map((r, j) => j === i ? { ...r, valor_segmento: e.target.value || undefined } : r))} className="border rounded px-2 py-1 text-sm flex-1"><option value="">Modalidad</option>{modalidades.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}</select>}
              {s.tipo_segmento === "tipo_seguro" && <select value={s.valor_segmento ?? ""} onChange={(e) => setSegRules((prev) => prev.map((r, j) => j === i ? { ...r, valor_segmento: e.target.value || undefined } : r))} className="border rounded px-2 py-1 text-sm flex-1"><option value="">-</option><option value="diario">Diario</option><option value="anual">Anual</option></select>}
              {segRules.length > 1 && (
                <button type="button" aria-label="Eliminar regla" onClick={() => setSegRules((prev) => prev.filter((_, j) => j !== i))} className="text-red-600">×</button>
              )}
            </div>
          ))}
        </div>
      </Modal>

      <ConfirmDialog isOpen={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Eliminar anuncio" message="¿Eliminar este anuncio?" confirmLabel="Eliminar" variant="danger" />
    </div>
  );
}
